/**
 * Card de pendência no Portal do Parceiro — UI-3 da reunião de 22/07/2026.
 *
 * [40:05]–[40:53]: *"quando você pendenciar, vai aparecer aqui para ele... vai
 * ter um botão. mandar a foto. Para ele re-anexar a foto."*
 *
 * Duas decisões de conteúdo que importam mais que o layout:
 *
 * 1. **O motivo aparece por extenso, não como código.** O parceiro precisa
 *    saber O QUE corrigir; sem isso ele reenvia o mesmo erro e a pendência
 *    volta — duas viagens onde cabia uma.
 * 2. **O card é a coisa mais visível da tela quando existe.** A pendência só
 *    sai quando ELE age; se ficar discreta entre os cartões de histórico, o
 *    comprovante fica parado esperando alguém que não sabe que precisa agir.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Camera } from "lucide-react";
import type { PendenciaMotivo } from "@/types/lotese";

/** Rótulos do checklist. Espelha PENDENCIA_MOTIVO_LABEL de types/lotese, mas
 * redigido para o PARCEIRO — o operador lê "Valor digitado difere do valor da
 * imagem"; o parceiro lê a mesma coisa em segunda pessoa.
 *
 * Tipado como `Record<PendenciaMotivo, …>` e não `Record<string, …>` de
 * propósito: com `string` o compilador aceitava o mapa incompleto, e o
 * `?? m` da linha 105 renderizava o CÓDIGO CRU ("valor_nao_localizado") na
 * tela do parceiro. O fallback continua ali para código desconhecido vindo da
 * API, mas agora o `tsc` cobra o rótulo de todo motivo que existe. */
const MOTIVO_LABEL_PARCEIRO: Record<PendenciaMotivo, string> = {
  valor_divergente: "O valor que você digitou não bate com o valor da foto",
  foto_ilegivel: "Não foi possível ler a foto — ficou ilegível ou fora de foco",
  data_ilegivel: "Não foi possível identificar a data no comprovante",
  ponto_incorreto: "O terminal selecionado não corresponde ao comprovante",
  valor_nao_localizado: "Não encontramos esse valor no extrato do GES — confira o comprovante",
  comprovante_duplicado: "Este comprovante é de outro depósito ou já foi enviado",
};

export interface PendenciaPortal {
  motivos: string[];
  descricao: string | null;
  criada_em: string | null;
  aberta: boolean;
}

interface Props {
  pendencias: {
    id: string;
    ges_ponto_codigo: string;
    ges_ponto_nome: string;
    valor: number;
    pendencia: PendenciaPortal;
  }[];
  onReenviar: (comprovanteId: string, gesPontoCodigo: string) => void;
}

export function PendenciaCard({ pendencias, onReenviar }: Props) {
  if (pendencias.length === 0) return null;

  return (
    <Card className="border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-amber-900 dark:text-amber-200">
          <AlertTriangle className="h-5 w-5" />
          {pendencias.length === 1
            ? "1 comprovante precisa ser reenviado"
            : `${pendencias.length} comprovantes precisam ser reenviados`}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {pendencias.map((c) => (
          <div
            key={c.id}
            className="rounded-lg border border-amber-300 bg-background p-3 dark:border-amber-800"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <span>{c.ges_ponto_codigo}</span>
                  <span className="truncate font-normal text-muted-foreground">
                    {c.ges_ponto_nome}
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Enviado como{" "}
                  {new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(c.valor)}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => onReenviar(c.id, c.ges_ponto_codigo)}
                className="shrink-0"
              >
                <Camera className="mr-1.5 h-4 w-4" />
                Enviar nova foto
              </Button>
            </div>

            <ul className="mt-2 space-y-1">
              {c.pendencia.motivos.map((m) => (
                <li key={m} className="flex items-start gap-1.5 text-sm">
                  <Badge
                    variant="outline"
                    className="mt-0.5 shrink-0 border-amber-400 px-1 py-0 text-[10px] text-amber-800 dark:text-amber-300"
                  >
                    !
                  </Badge>
                  {/* `motivos` chega da API como string[] — o cast é só para o
                      lookup; o `?? m` cobre um código que o backend passe a
                      emitir antes de o front ser atualizado. */}
                  <span>{MOTIVO_LABEL_PARCEIRO[m as PendenciaMotivo] ?? m}</span>
                </li>
              ))}
            </ul>

            {c.pendencia.descricao && (
              <p className="mt-2 rounded bg-muted/60 p-2 text-sm italic">
                “{c.pendencia.descricao}”
              </p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
